import {
  SeededEmailComposerHost,
  type SeededEmailComposerRequest,
} from '@/components/affiliate/bulk-email/SeededEmailComposerHost';
import { useCompanionPanelStore } from '@/stores/companion-panel-store';
import { useMemo } from 'react';
import { useCompanionEmailRecipient } from './useCompanionEmailRecipient';

/**
 * Where a companion card's "write to this affiliate" gesture actually opens.
 *
 * A thin adapter over the shared `SeededEmailComposerHost`: it resolves the
 * card's recipient (name known immediately, address filled in once the
 * picker's own query answers) and maps the companion-shaped request onto the
 * composer's own shape. Any other surface that needs the same composer
 * mounts `SeededEmailComposerHost` directly instead of going through the
 * companion store.
 */
export const CompanionEmailCtaHost = () => {
  const request = useCompanionPanelStore((state) => state.emailCta);
  const closeEmailCta = useCompanionPanelStore((state) => state.closeEmailCta);
  const recipient = useCompanionEmailRecipient(request);

  // Built once per actual change, never once per render: the host writes the
  // shared selection store from this object, and a fresh identity on every
  // render would rewrite that store on every render.
  const actionId = request?.actionId;
  const programId = request?.programId ?? null;
  const draft = request?.draft ?? null;
  const composerRequest = useMemo<SeededEmailComposerRequest | null>(
    () => (actionId === undefined ? null : { seedKey: actionId, recipient, programId, draft }),
    [actionId, recipient, programId, draft]
  );

  return <SeededEmailComposerHost request={composerRequest} onClose={closeEmailCta} />;
};
